import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { ReportUserSignature } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { useAppSelector } from '@/shared/hooks';
import { getFirstValidJobPosition, getImageSrc, getName } from '@/shared/lib';

import { i18n, organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { FooterDate } from '../FooterDate/FooterDate';

import styles from './Signature.module.scss';

type SignatureProps = {
  className?: string;
  testID?: string;
  signature: ReportUserSignature;
  blackAndWhiteMode?: boolean;
  descriptionLabel: string;
  userTimezone: string;
};

export const Signature: FC<SignatureProps> = (props) => {
  const {
    signature,
    className,
    testID,
    blackAndWhiteMode,
    descriptionLabel,
    userTimezone,
  } = props;

  const { formatMessage, formatDate } = useIntl();

  const organizationStampAssetID = useAppSelector(
    organizationModel.selectors.selectOrganizationStampAssetID,
  );

  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const {
    UserFirstName,
    UserLastName,
    SignatureAssetID,
    StampAssetID: userStampAssetID,
    JobPositions,
  } = signature;

  const speciality = getFirstValidJobPosition(JobPositions);

  const isSignatureImageExists = Boolean(SignatureAssetID);
  const isUserStampImageExists = Boolean(userStampAssetID);
  const isOrganizationStampExists = Boolean(organizationStampAssetID);

  const signatureImageSrc = getImageSrc(SignatureAssetID, 'original');
  const userStampImageSrc = getImageSrc(userStampAssetID, 'original');
  const organizationStampImage = getImageSrc(
    organizationStampAssetID ?? '',
    'original',
  );

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="preview-signature"
    >
      <div className={styles.section}>
        <div className={styles.nameAndSpeciality}>
          <h2 className={cn(styles.signedUserName, 'h6')}>
            {getName(UserFirstName, UserLastName, { userLocale })}
          </h2>

          {speciality && (
            <p className={cn(styles.speciality, 'p3t')}>
              {formatMessage(i18n.specialityType[speciality])}
            </p>
          )}
        </div>

        {isSignatureImageExists && (
          <div
            className={cn(
              styles.imgFrame,
              styles.imageSection,
              styles.signatureImageSection,
            )}
          >
            <img
              src={signatureImageSrc}
              alt={formatMessage({
                id: 'global.signature',
                defaultMessage: 'Signature',
              })}
              className={cn(
                styles.signatureOrStampImage,
                blackAndWhiteMode && styles.blackAndWhiteMode,
              )}
            />
          </div>
        )}
      </div>

      <div className={styles.section}>
        <FooterDate
          date={formatDate(signature.SignedAt?.toDate() ?? 0, {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: userTimezone,
          })}
          label={descriptionLabel}
          className={styles.footerDate}
        />

        <div className={cn(styles.wrapperStamps, styles.imageSection)}>
          {isUserStampImageExists && (
            <div className={styles.imgFrame}>
              <img
                src={userStampImageSrc}
                alt={formatMessage({
                  id: 'imgAltText.stamp',
                  defaultMessage: 'Stamp',
                })}
                className={cn(
                  styles.signatureOrStampImage,
                  blackAndWhiteMode && styles.blackAndWhiteMode,
                )}
              />
            </div>
          )}

          {isOrganizationStampExists && (
            <div className={styles.imgFrame}>
              <img
                src={organizationStampImage}
                alt={formatMessage({
                  id: 'imgAltText.stamp',
                  defaultMessage: 'Stamp',
                })}
                className={cn(
                  styles.signatureOrStampImage,
                  blackAndWhiteMode && styles.blackAndWhiteMode,
                )}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
